import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AxiosError } from 'axios';
import api, { getCsrfCookie } from '@/lib/api';
import { setSentryUser } from '@/lib/sentry';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ApiErrorResponse,
} from '@/types';

/**
 * POST /email/send-verification-code. `alreadySent` means a code went out
 * within the resend cooldown, so no new email was sent; `retryAfter` is the
 * seconds until another send is allowed (see AuthController::sendVerificationCode).
 */
export interface SendVerificationCodeResponse {
  message: string;
  alreadySent: boolean;
  retryAfter: number;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (credentials: LoginRequest) => Promise<void>;
  register: (
    data: RegisterRequest,
  ) => Promise<{ message: string; email: string }>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<string>;
  resetPassword: (data: ResetPasswordRequest) => Promise<string>;
  sendVerificationCode: (email: string) => Promise<SendVerificationCodeResponse>;
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  clearAuth: () => void;
}

// ---------------------------------------------------------------------------
// Logout callback registry
// Other stores register cleanup functions here to avoid circular imports.
// ---------------------------------------------------------------------------
const _onLogoutCallbacks: Array<() => void> = [];

/** Register a function to be called whenever the user logs out or auth is cleared. */
export function onLogout(callback: () => void): () => void {
  _onLogoutCallbacks.push(callback);
  return () => {
    const idx = _onLogoutCallbacks.indexOf(callback);
    if (idx !== -1) _onLogoutCallbacks.splice(idx, 1);
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          await getCsrfCookie();
          const { data } = await api.post<LoginResponse>('/login', credentials);
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
          });
          setSentryUser(data.user);
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterRequest) => {
        set({ isLoading: true });
        try {
          await getCsrfCookie();
          const { data: responseData } = await api.post<{
            message: string;
            email: string;
          }>('/register', data);
          set({ isLoading: false });
          return responseData;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      sendVerificationCode: async (email: string) => {
        const { data } = await api.post<SendVerificationCodeResponse>(
          '/email/send-verification-code',
          { email },
        );
        return data;
      },

      verifyEmailCode: async (email: string, code: string) => {
        await getCsrfCookie();
        const { data } = await api.post<LoginResponse>('/email/verify-code', {
          email,
          code,
        });
        set({ user: data.user, isAuthenticated: true });
        setSentryUser(data.user);
      },

      forgotPassword: async (data: ForgotPasswordRequest) => {
        set({ isLoading: true });
        try {
          await getCsrfCookie();
          const { data: responseData } = await api.post<{ message: string }>(
            '/forgot-password',
            data,
          );
          set({ isLoading: false });
          return responseData.message;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resetPassword: async (data: ResetPasswordRequest) => {
        set({ isLoading: true });
        try {
          await getCsrfCookie();
          const { data: responseData } = await api.post<{ status: string }>(
            '/reset-password',
            data,
          );
          set({ isLoading: false });
          return responseData.status;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ user: null, isAuthenticated: false });
        setSentryUser(null);
        // Notify registered cleanup callbacks (e.g. groupStore.clearGroups)
        _onLogoutCallbacks.forEach((cb) => cb());
        try {
          await api.post('/logout');
        } catch {
          // Ignore — local state already cleared
        }
      },

      setUser: (user: User) => set({ user }),

      clearAuth: () => {
        set({ user: null, isAuthenticated: false });
        _onLogoutCallbacks.forEach((cb) => cb());
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.response?.data) {
      const data = error.response.data as ApiErrorResponse;
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : String(firstError);
      }
      if (data.message) return data.message;
      if (data.error) return data.error;
    }
    // Request was sent but no response arrived (network failure, timeout, CORS, etc.)
    if (error.request) {
      if (error.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
      return 'Network error. Please check your connection and try again.';
    }
  }
  return 'An unexpected error occurred. Please try again.';
}
